import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {StyleSheet, View, Text, Pressable} from 'react-native';
import {useState, useEffect, useCallback, Fragment} from 'react';
import {useDispatch} from 'react-redux';
import {useToast} from 'react-native-toast-notifications';
import {useModalUpsellFeature} from 'interface/hooks/useModalUpsellFeature';
import {IconButton} from 'interface/base/IconButton';
import {bytesize} from 'utils/common/data';
import {bulkDownload, bulkDownloadPreview} from 'utils/mediafire';
import {colors, vectors} from 'config/styles';
import {theme} from 'config/themes';
import {FilesErrors} from 'store/slices/files/types';
import {slices} from 'store';

export interface BulkDownloadProps {
  ids: string[],
  exit: () => void,
}

export function ModalBulkDownload(props: BulkDownloadProps) {
  const {t} = useLingui();
  const [showUpsell] = useModalUpsellFeature('pro', 'bulkdownload');
  const [isDownloadReady, setDownloadReady] = useState(false);
  const [isLargeDownload, promptLargeDownload] = useState(false);
  const [isUserCharged, promptUseBandwidth] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [statusKey, setStatusKey] = useState('');
  const [fileName, setFileName] = useState('');

  const toast = useToast();

  const dispatch = useDispatch();

  const submit = useCallback(async () => {
    const res = await bulkDownload(props.ids, statusKey, isUserCharged, isLargeDownload);
    if (res && res['result'] === 'Success') {
      dispatch(slices.transfers.actions.downloadBulk({statusKey, fileName, totalSize}));
      props.exit();
    } else {
      if (res && res.error === FilesErrors.ZipTotalSizeTooBig) {
        promptLargeDownload(true);
      } else if (res && (res.error === FilesErrors.ZipOwnerInsufficientBandwidth
        || res.error === FilesErrors.ZipOwnerNotPro)) {
        promptUseBandwidth(true);
      } else {
        toast.show(res?.message ?? t`Failed to start bulk download`, {type: 'danger'});
      }
    }
  }, [props.ids, statusKey, isLargeDownload, isUserCharged]);

  useEffect(() => {
    bulkDownloadPreview(props.ids).then((res) => {
      if (res && res.result === 'Success') {
        setTotalSize(res.totalSizeEstimate);
        setTotalItems(res.fileCount);
        setStatusKey(res.statusKey);
        setFileName(res.fileName);
        setDownloadReady(true);
      } else {
        if (res.error && res.message) {
          props.exit();
          if (res.error === 148) {
            showUpsell();
          } else {
            toast.show(res?.message ?? 'Failed to start bulk download', {type: 'danger'});
          }
        }
      }
    });
  }, [props.ids]);

  return (
    <View style={styles.root}>
      <View style={styles.bar}>
        <Text style={styles.title}>
          {t`Bulk Download`}
        </Text>
        <Pressable style={styles.close} onPress={props.exit}>
          <Svg viewBox="0 0 14 14" width={14} height={14}>
            <Path d={vectors.icons.close} fill={colors.neutral.mid}/>
          </Svg>
        </Pressable>
      </View>
      <View style={styles.content}>
        {!isLargeDownload && !isUserCharged &&
          <View style={styles.messageWrapper}>
            {!isDownloadReady &&
              <Text style={styles.message}>
                {t`Calculating total size…`}
              </Text>
            }
            {isDownloadReady && <Fragment>
              <Text style={styles.message}>
                {`Are you sure you want to zip `}
                <Text style={styles.messageBold}>
                  {`${totalItems} ${totalItems === 1 ? 'item' : 'items'}`}
                </Text>
                <Text>?</Text>
              </Text>
              <Text style={styles.message}>
                {'Total size: '}
                {totalSize > 0 &&
                  <Text style={styles.messageBold}>
                    {bytesize(totalSize)}
                  </Text>
                }
              </Text>
            </Fragment>}
            <Text style={[styles.message, styles.marginTop]}>
              {t`Depending on the size of the folder it may take several minutes to prepare the bulk download.`}
            </Text>
          </View>
        }
        {isLargeDownload && !isUserCharged &&
          <View style={styles.messageWrapper}>
            <Text style={styles.message}>
              {t`The total size of these files exceeds 2GB. Would you like to continue with the bulk download?`}
            </Text>
          </View>
        }
        {isUserCharged && !isLargeDownload &&
          <View style={styles.messageWrapper}>
            <Text style={styles.message}>
              {t`The owner does not have sufficient bandwidth, would you like to use your own bandwidth to continue with the bulk download?`}
            </Text>
          </View>
        }
      </View>
      <View style={styles.buttons}>
        {!isLargeDownload && !isUserCharged &&
          <IconButton
            mode="primary"
            text={t`Download`.toUpperCase()}
            onPress={submit}
            disabled={!isDownloadReady}
          />
        }
        {(isLargeDownload || isUserCharged) &&
          <IconButton
            mode="primary"
            text={t`Confirm`.toUpperCase()}
            onPress={submit}
          />
        }
        <View style={styles.spacer}/>
        <IconButton
          mode="cancel"
          text={t`Close`.toUpperCase()}
          onPress={props.exit}
        />
      </View>
    </View>
  );
}

export const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: theme.dialog.fill,
  },
  content: {
    width: '100%',
    padding: 20,
    paddingTop: 8,
  },
  message: {
    flex: 1,
    fontSize: 14,
    lineHeight: 24,
    color: theme.dialog.text,
  },
  messageBold: {
    fontWeight: '600',
  },
  marginTop: {
    marginTop: 16,
  },
  messageWrapper: {
    flex: 1,
  },
  buttons: {
    flex: 0,
    flexDirection: 'row-reverse',
    padding: 20,
  },
  spacer: {
    width: 6,
  },
  bar: {
    width: '100%',
  },
  title: {
    marginLeft: 20,
    lineHeight: 65,
    fontSize: 20,
    fontWeight: '300',
    color: theme.dialog.title,
  },
  close: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    height: 65,
    width: 58,
    right: 1,
    top: -2,
  },
});

